/**
 * Visit "Reschedule" Drawer (Phase 7.C)
 *
 * ESM replacement for the inline ``<script>`` that used to live in
 * ``templates/office/visits/partials/_reschedule_drawer.html``.
 *
 * Bespoke widgets handled here:
 *
 *   1. Date+time → hidden ISO datetime sync. The form ships a
 *      Flowbite-formatted ``[datepicker]`` (dd/mm/yyyy) and a native
 *      ``<input type="time">``; on every change/changeDate we
 *      reconstruct ``yyyy-mm-ddThh:mm`` into the hidden field that
 *      the backend actually reads.
 *
 *   2. Real-time inline error clearing. Once both date and time are
 *      filled, the "Please select a date and time" error hides
 *      automatically — same UX as the Customer create form.
 *
 *   3. Cleaner-picker selection listener. The picker fires a custom
 *      ``cleanerSelectionChanged`` event with ``detail.selectedCleaners``;
 *      we use it to clear the picker's inline error as soon as the
 *      user adds a cleaner.
 *
 *   4. Drawer-scoped Flowbite boot. ``initFlowbite()`` instantiates
 *      the Datepicker on the new ``[datepicker]`` input (Flowbite's
 *      auto-init only runs once on page load and can't see fetched
 *      drawer content).
 *
 *   5. Submit-time validators (validateDateTime + required-field
 *      validator on duration), wired into ``wireDrawerActionForm``
 *      with ``expectJson: true`` (the reschedule view returns
 *      ``{success: true|false, error?, message?}``).
 *
 * Markup contract — the partial is fully inline-JS-free:
 *
 *   <div data-bespoke-init="visitReschedule"
 *        data-reschedule-title="Reschedule Visit"
 *        data-reschedule-select-datetime="Please select a date and time"
 *        data-reschedule-duration-required="Duration is required"
 *        data-reschedule-success="Visit rescheduled successfully"
 *        data-reschedule-error="Error rescheduling visit">
 *     <form id="drawerRescheduleForm" …>
 *       <input id="drawer_scheduled_date" datepicker …>
 *       <input id="drawer_scheduled_time" type="time" …>
 *       <input id="drawer_scheduled_start_datetime" type="hidden" …>
 *       <input id="drawer_scheduled_duration" …>
 *       <p id="drawerRescheduleDateTimeError" class="hidden …"></p>
 *       <p id="drawerRescheduleDurationError" class="hidden …"></p>
 *       <p id="drawerRescheduleCleanerError" class="hidden …"></p>
 *       <div id="drawer_reschedule_picker">…</div>
 *     </form>
 *     <button id="drawerRescheduleSubmit" …>…</button>
 *   </div>
 */

function initVisitRescheduleDrawer(rootEl) {
    if (!rootEl) return;
    const ds = rootEl.dataset;

    const titleEl = document.getElementById('drawerLabel');
    if (titleEl && ds.rescheduleTitle) {
        titleEl.textContent = ds.rescheduleTitle;
    }

    const dateInput = rootEl.querySelector('#drawer_scheduled_date');
    const timeInput = rootEl.querySelector('#drawer_scheduled_time');
    const hiddenInput = rootEl.querySelector('#drawer_scheduled_start_datetime');
    const dateTimeErrorEl = rootEl.querySelector('#drawerRescheduleDateTimeError');

    function updateHiddenDateTime() {
        if (!dateInput || !timeInput || !hiddenInput) return;
        const dateVal = dateInput.value;
        const timeVal = timeInput.value;
        if (dateVal && timeVal) {
            const parts = dateVal.split('/');
            if (parts.length === 3) {
                const day = parts[0].padStart(2, '0');
                const month = parts[1].padStart(2, '0');
                const year = parts[2];
                hiddenInput.value = `${year}-${month}-${day}T${timeVal}`;
            }
        } else {
            hiddenInput.value = '';
        }
    }

    function clearDateTimeError() {
        if (!dateInput || !timeInput || !dateTimeErrorEl) return;
        if (dateInput.value && timeInput.value) {
            dateTimeErrorEl.classList.add('hidden');
            dateInput.classList.remove('border-red-500');
            timeInput.classList.remove('border-red-500');
        }
    }

    if (dateInput) {
        ['change', 'changeDate'].forEach((evt) => dateInput.addEventListener(evt, updateHiddenDateTime));
        ['change', 'changeDate', 'blur'].forEach((evt) => dateInput.addEventListener(evt, clearDateTimeError));
    }
    if (timeInput) {
        timeInput.addEventListener('change', updateHiddenDateTime);
        ['change', 'input'].forEach((evt) => timeInput.addEventListener(evt, clearDateTimeError));
    }

    const cleanerErrorEl = rootEl.querySelector('#drawerRescheduleCleanerError');
    const reschedulePicker = rootEl.querySelector('#drawer_reschedule_picker');
    if (reschedulePicker) {
        reschedulePicker.addEventListener('cleanerSelectionChanged', (e) => {
            if (e.detail && e.detail.selectedCleaners && e.detail.selectedCleaners.length > 0) {
                if (cleanerErrorEl) cleanerErrorEl.classList.add('hidden');
                const wrapper = reschedulePicker.querySelector('.flex.flex-wrap.items-center');
                if (wrapper) wrapper.classList.remove('border-red-500');
            }
        });
    }

    if (typeof window.initFlowbite === 'function') {
        setTimeout(window.initFlowbite, 100);
    }

    if (typeof window.wireDrawerActionForm !== 'function') return;
    if (typeof window.requiredFieldValidator !== 'function') return;

    function validateDateTime() {
        updateHiddenDateTime();
        if (hiddenInput && hiddenInput.value) return true;
        if (dateTimeErrorEl) {
            dateTimeErrorEl.textContent = ds.rescheduleSelectDatetime || '';
            dateTimeErrorEl.classList.remove('hidden');
        }
        return false;
    }

    const validateDuration = window.requiredFieldValidator({
        inputId: 'drawer_scheduled_duration',
        errorId: 'drawerRescheduleDurationError',
        message: ds.rescheduleDurationRequired || '',
    });

    window.wireDrawerActionForm({
        formId: 'drawerRescheduleForm',
        submitBtnId: 'drawerRescheduleSubmit',
        validators: [validateDateTime, validateDuration],
        expectJson: true,
        successMessage: ds.rescheduleSuccess || '',
        errorMessage: ds.rescheduleError || '',
        errorContainerId: 'drawerRescheduleDateTimeError',
        // Inline-only error UX (no toast). The drawer has a dedicated
        // error container directly under the date/time inputs so the
        // toast was redundant noise — and it covered the calendar
        // behind the drawer. This matches the pattern adopted for
        // visit-cleaner-absent-drawer.js. Drag-drop reschedule keeps
        // its toast (it has no form / no inline container).
        onError: (error, { showFormError }) => {
            showFormError(error.message || ds.rescheduleError || '');
        },
    });
}

if (typeof window !== 'undefined') {
    window.visitDrawerInits = window.visitDrawerInits || {};
    window.visitDrawerInits.visitReschedule = initVisitRescheduleDrawer;
}

export { initVisitRescheduleDrawer };
